import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, TextField, Avatar, IconButton, Button, FormControl, InputLabel, Select, MenuItem, Chip, Box, Popover, Typography, Autocomplete, Snackbar, Alert, Stack, Paper } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { ExpandMore, ExpandLess, Settings as SettingsIcon, Add as AddIcon } from "@mui/icons-material";
import InputMask from "react-input-mask"; // Para aplicar máscaras
import debounce from "lodash.debounce";
import { fetchData } from "../../api/client";
import PhoneField from "../PhoneField";
import {
  formatStoredPhoneForInput,
  getPhoneValidationMessage,
  toE164Digits,
  isPhoneSentinel,
  isUsableStoredPhone,
} from "../../utils/phone";
import { useClients } from "../../hooks/useClients";
import { useClientsGroups } from "../../hooks/useClientsGroups";
import { useClientTags } from "../../hooks/useClientTags";
import { useClientRelationships } from "../../hooks/useClientRelationships";
import { getClientProfilePictureUrl, getInitials } from "../../utils/utils";
import {
  getClientRelationshipDirectionLabel,
  getClientRelationshipPeer,
  getClientRelationshipTypeLabel,
} from "../../utils/clientRelationships";
import DuplicateClientDialog from "../clients/DuplicateClientDialog";
import ExternalRegistrationButton, {
  useExternalRegistrationUrl,
} from "../clients/ExternalRegistrationButton";
import CustomFieldRow, {
  getVisibleFieldDefinitions,
} from "../CustomFieldsBlock";
import { useFieldDefinitions } from "../../hooks/useFieldDefinitions";
import { getClientPhoneRaw } from "../../utils/clientContactUtils";
import {
  getClientCommercialName,
  isPessoaJuridica,
} from "../../utils/clientName";

const NIVEL_CONSCIENCIA_EXPLICACAO =
  "Os níveis de consciência são etapas que representam em que estágio do relacionamento ou da jornada de compra o cliente se encontra (ex.: consciente do problema, considerando soluções, decisão). Eles ajudam a organizar o pipeline e o atendimento.";

const SEXO_NUM_TO_LABEL = { 1: "Masculino", 2: "Feminino", 3: "Outro" };
const SEXO_LABEL_TO_NUM = { Masculino: 1, Feminino: 2, Outro: 3, Outros: 3 };

const normalizeBirthDateForInput = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s || s.startsWith("0000")) return "";
  if (s.includes("T")) return s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
};

const sexoToLabel = (raw) => {
  const n = Number(raw);
  if (Number.isFinite(n) && SEXO_NUM_TO_LABEL[n]) return SEXO_NUM_TO_LABEL[n];
  const s = String(raw ?? "").trim();
  if (SEXO_LABEL_TO_NUM[s] != null) return s;
  return "";
};

const CustomerDetails = ({ selectedConversation, onSelectedConversationUpdate }) => {
  const navigate = useNavigate();
  const [nivelConscienciaAnchor, setNivelConscienciaAnchor] = useState(null);
  const [dupDialog, setDupDialog] = useState({
    open: false,
    duplicates: [],
    attemptedPayload: null,
    secondaryClientId: null,
  });

  const parseTags = (str) => {
    if (!str || typeof str !== "string") return [];
    return str.split(",").map((t) => t.trim()).filter(Boolean);
  };
  const tagsToString = (arr) => (Array.isArray(arr) && arr.length ? arr.join(", ") : "");

  const formatPhoneField = (value) =>
    isUsableStoredPhone(value) ? formatStoredPhoneForInput(String(value)) : "";

  const [customerDetails, setCustomerDetails] = useState({
    nome: "",
    razaosocial: "",
    email: "",
    telefone: "",
    celular: "",
    whatsapp: "",
    cpf: "",
    cnpj: "",
    identidade: "",
    data_nascimento: "",
    data_abertura: "",
    nome_responsavel: "",
    cargo_responsavel: "",
    tamanho_frota: "",
    inscricao_estadual: "",
    inscricao_estadual_isento: 0,
    sexo: "",
    id_facebook: "",
    id_instagram: "",
    id_linkedin: "",
    id_grupo: null,
    tags: [],
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
    potencial: 1,
    imagem: "",
  });
  const [tagInput, setTagInput] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [tagSnack, setTagSnack] = useState({ open: false, message: "" });

  const { data: tagOptions } = useClientTags({ search: tagSearch, limit: 200, includeUsed: false });
  const allowedTagNames = useMemo(
    () =>
      (tagOptions || [])
        .filter((t) => (t?.status == null ? true : Number(t.status) === 1))
        .map((t) => String(t?.nome || "").trim())
        .filter(Boolean),
    [tagOptions]
  );

  const [errors, setErrors] = useState({});
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const { updateMutation } = useClients();
  const { data: groupsData } = useClientsGroups();
  const groups = Array.isArray(groupsData) ? groupsData : [];

  const { data: fieldDefs, isLoading: fieldDefsLoading } = useFieldDefinitions({
    enabled: !!selectedConversation?.clientId,
  });

  const clientId = selectedConversation?.clientId;
  const { data: relationships = [] } = useClientRelationships(clientId);

  const clientCustomValues = useMemo(
    () => ({
      campo_adicional1_sim_nao: selectedConversation?.campo_adicional1_sim_nao ?? 0,
      campo_adicional2_sim_nao: selectedConversation?.campo_adicional2_sim_nao ?? 0,
      campo_adicional3_multiselect:
        selectedConversation?.campo_adicional3_multiselect ?? "",
      ...(selectedConversation?.clientCustomFields || {}),
    }),
    [selectedConversation]
  );

  const handleClientCustomFieldCommit = (key, value) => {
    const def = (fieldDefs?.client || []).find((d) => d.key === key);
    let payload = {};
    let updateLocal = {};
    if (!def || def.legacy) {
      payload = { [key]: value };
      updateLocal = { [key]: value };
    } else {
      payload = { customFields: { [key]: value } };
      updateLocal = {
        clientCustomFields: {
          ...(selectedConversation?.clientCustomFields || {}),
          [key]: value,
        },
      };
    }
    updateFn(payload);
    onSelectedConversationUpdate?.(updateLocal);
  };

  const isClientPJ = useMemo(() => {
    return isPessoaJuridica(
      {
        tipo: selectedConversation?.clientType ?? selectedConversation?.client?.clientType,
        clientType: selectedConversation?.clientType ?? selectedConversation?.client?.clientType,
        cnpj:
          selectedConversation?.cnpj ||
          selectedConversation?.client?.cnpj ||
          customerDetails.cnpj ||
          "",
        cpf:
          selectedConversation?.cpf ||
          selectedConversation?.client?.cpf ||
          customerDetails.cpf ||
          "",
        razaosocial:
          selectedConversation?.razaosocial ||
          selectedConversation?.client?.companyName ||
          customerDetails.razaosocial ||
          "",
        nome: customerDetails.nome || selectedConversation?.nome || "",
      },
      { allowEcomTipoAmbiguity: true }
    );
  }, [
    selectedConversation?.clientType,
    selectedConversation?.client?.clientType,
    selectedConversation?.cnpj,
    selectedConversation?.client?.cnpj,
    selectedConversation?.razaosocial,
    selectedConversation?.client?.companyName,
    selectedConversation?.cpf,
    selectedConversation?.client?.cpf,
    selectedConversation?.nome,
    customerDetails.cnpj,
    customerDetails.cpf,
    customerDetails.razaosocial,
    customerDetails.nome,
  ]);

  useEffect(() => {
    if (selectedConversation) {
      const clientTagsRaw =
        selectedConversation?.clientTags ?? selectedConversation?.client?.tags ?? "";
      setCustomerDetails({
        nome: selectedConversation?.nome || "",
        razaosocial:
          selectedConversation?.razaosocial ||
          selectedConversation?.client?.companyName ||
          "",
        email: selectedConversation?.email || "",
        telefone: formatPhoneField(
          selectedConversation?.telefone ?? selectedConversation?.client?.telephone
        ),
        celular: formatPhoneField(
          selectedConversation?.celular ??
            selectedConversation?.client?.mobile ??
            getClientPhoneRaw(selectedConversation)
        ),
        whatsapp: formatPhoneField(
          selectedConversation?.whatsapp ??
            selectedConversation?.whatsappPhone ??
            selectedConversation?.client?.whatsapp ??
            selectedConversation?.client?.whatsappPhone
        ),
        cpf: selectedConversation?.cpf ?? selectedConversation?.client?.cpf ?? "",
        cnpj: selectedConversation?.cnpj ?? selectedConversation?.client?.cnpj ?? "",
        identidade:
          selectedConversation?.identidade ??
          selectedConversation?.client?.identityDocument ??
          "",
        data_nascimento: normalizeBirthDateForInput(
          selectedConversation?.data_nascimento ?? selectedConversation?.client?.birthDate
        ),
        data_abertura: normalizeBirthDateForInput(
          selectedConversation?.data_abertura ?? selectedConversation?.client?.openingDate
        ),
        nome_responsavel:
          selectedConversation?.nome_responsavel ??
          selectedConversation?.client?.responsibleName ??
          "",
        cargo_responsavel:
          selectedConversation?.cargo_responsavel ??
          selectedConversation?.client?.responsibleRole ??
          "",
        tamanho_frota:
          selectedConversation?.tamanho_frota ??
          selectedConversation?.client?.fleetSize ??
          "",
        inscricao_estadual:
          selectedConversation?.inscricao_estadual ??
          selectedConversation?.client?.stateRegistration ??
          "",
        inscricao_estadual_isento: Number(
          selectedConversation?.inscricao_estadual_isento ??
            selectedConversation?.client?.stateRegistrationExempt
        )
          ? 1
          : 0,
        sexo: sexoToLabel(
          selectedConversation?.sexo ?? selectedConversation?.client?.gender
        ),
        id_facebook:
          selectedConversation?.id_facebook ?? selectedConversation?.client?.facebook ?? "",
        id_instagram:
          selectedConversation?.id_instagram ?? selectedConversation?.client?.instagram ?? "",
        id_linkedin:
          selectedConversation?.id_linkedin ?? selectedConversation?.client?.linkedin ?? "",
        id_grupo: selectedConversation?.id_grupo ?? null,
        tags: parseTags(clientTagsRaw),
        cep: selectedConversation?.cep || "",
        endereco: selectedConversation?.endereco || "",
        numero: selectedConversation?.numero || "",
        complemento: selectedConversation?.complemento || "",
        bairro: selectedConversation?.bairro || "",
        cidade: selectedConversation?.cidade || "",
        uf: selectedConversation?.uf || "",
        potencial: selectedConversation?.clientPotential || 1,
        imagem:
          selectedConversation?.client?.profile_picture ??
          selectedConversation?.client?.imagem ??
          selectedConversation?.clientImage ??
          selectedConversation?.imagem ??
          "",
      });
    }
  }, [selectedConversation]);

  // Função para atualizar no backend (debounced)
  const updateFn = useCallback(
    (updatedDetails) => {
      if (selectedConversation?.clientId) {
        const attemptedPayload = {
          id_projeto: selectedConversation?.projectId ?? selectedConversation?.id_projeto ?? null,
          ...(updatedDetails || {}),
        };
        updateMutation.mutate(
          { id: selectedConversation.clientId, ...updatedDetails },
          {
            onError: (err) => {
              if (err?.code === "errors.clients.duplicate") {
                setDupDialog({
                  open: true,
                  duplicates: err?.data?.duplicates || [],
                  attemptedPayload,
                  secondaryClientId: Number(selectedConversation.clientId),
                });
              }
            },
          }
        );
      }
    },
    [selectedConversation?.clientId, updateMutation]
  );
  const debouncedUpdate = useMemo(() => debounce(updateFn, 500), [updateFn]);

  // Função para validar os campos
  const validateField = (field, value) => {
    if (field === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "E-mail inválido";
    }
    if ((field === "celular" || field === "telefone" || field === "whatsapp") && value) {
      return getPhoneValidationMessage(value, "BR");
    }
    if (field === "cep" && value && !/^\d{5}-\d{3}$/.test(value)) {
      return "CEP deve estar no formato XXXXX-XXX";
    }
    if (field === "nome" && (!value || value.trim().length === 0)) {
      if (!isClientPJ) return "Nome é obrigatório";
      const razao = String(customerDetails.razaosocial || "").trim();
      if (!razao) return "Informe o nome fantasia ou a razão social";
    }
    if (field === "razaosocial" && isClientPJ && (!value || value.trim().length === 0)) {
      const fantasia = String(customerDetails.nome || "").trim();
      if (!fantasia) return "Razão social é obrigatória";
    }
    if (field === "uf" && value && value.length !== 2) {
      return "UF deve conter 2 caracteres";
    }
    return "";
  };

  // Atualiza os detalhes no estado
  const handleFieldChange = (field, value) => {
    const error = validateField(field, value);
    setErrors((prevErrors) => ({ ...prevErrors, [field]: error }));
    setCustomerDetails((prev) => ({ ...prev, [field]: value }));

    // Envia atualização apenas se não houver erro
    if (!error && field !== "cep") {
      let payload = field === "tags" ? { tags: tagsToString(value) } : { [field]: value };
      if (
        (field === "celular" || field === "telefone" || field === "whatsapp") &&
        value &&
        !isPhoneSentinel(String(value))
      ) {
        const e164 = toE164Digits(value, "BR");
        if (e164) {
          // API remapeia `whatsapp`→`numero_whatsapp` (coluna INT não aguenta telefone BR).
          payload = field === "whatsapp" ? { whatsapp: e164, numero_whatsapp: e164 } : { [field]: e164 };
        }
      }
      if (field === "sexo") {
        const num = SEXO_LABEL_TO_NUM[value];
        payload = { sexo: num != null ? num : null };
      }
      if (field === "data_nascimento") {
        payload = { data_nascimento: value ? String(value).slice(0, 10) : null };
      }
      if (field === "data_abertura") {
        payload = { data_abertura: value ? String(value).slice(0, 10) : null };
      }
      if (field === "inscricao_estadual_isento") {
        const exempt = Number(value) ? 1 : 0;
        payload = {
          inscricao_estadual_isento: exempt,
          ...(exempt ? { inscricao_estadual: null } : {}),
        };
        if (exempt) {
          setCustomerDetails((prev) => ({ ...prev, inscricao_estadual: "" }));
        }
      }
      // PJ: `nome` guarda fantasia; sem fantasia, espelha a razão social (mesma regra do AddClientForm).
      if (isClientPJ && field === "nome") {
        const fantasia = String(value ?? "").trim();
        const razao = String(customerDetails.razaosocial || "").trim();
        if (!fantasia && razao) {
          payload = { nome: razao };
          setCustomerDetails((prev) => ({ ...prev, nome: razao }));
        }
      }
      if (isClientPJ && field === "razaosocial") {
        const razao = String(value ?? "").trim();
        const fantasia = String(customerDetails.nome || "").trim();
        const prevRazao = String(
          selectedConversation?.razaosocial ||
            selectedConversation?.client?.companyName ||
            ""
        ).trim();
        if (razao && (!fantasia || fantasia === prevRazao)) {
          payload = { ...payload, nome: razao };
          setCustomerDetails((prev) => ({ ...prev, nome: razao }));
        }
      }
      debouncedUpdate(payload);
    }

    // Se o campo for "cep", tenta buscar o endereço automaticamente
    if (field === "cep" && value.length === 9 && !error) {
      fetchAddress(value);
    }
  };

  const applyTags = (nextTags) => {
    const cleaned = (Array.isArray(nextTags) ? nextTags : [])
      .map((x) => String(x || "").trim())
      .filter(Boolean);
    const uniq = Array.from(new Set(cleaned));
    setCustomerDetails((prev) => ({ ...prev, tags: uniq }));
    debouncedUpdate({ tags: tagsToString(uniq) });
    onSelectedConversationUpdate?.({ clientTags: tagsToString(uniq) });
  };

  const handleAddTag = () => {
    const t = String(tagInput || "").trim();
    if (!t) return;
    if (allowedTagNames.length && !allowedTagNames.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setTagSnack({ open: true, message: `Esta tag (${t}) não é permitida.` });
      setTagInput("");
      return;
    }
    const next = [...(customerDetails.tags || [])];
    if (next.includes(t)) {
      setTagInput("");
      return;
    }
    next.push(t);
    setTagInput("");
    applyTags(next);
  };

  const handleRemoveTag = (index) => {
    const next = (customerDetails.tags || []).filter((_, i) => i !== index);
    applyTags(next);
  };

  // Função para buscar o endereço com base no CEP
  const fetchAddress = async (cep) => {
    setIsFetchingAddress(true);
    try {
      const cleanCep = cep.replace("-", "");
      const res = await fetchData(`/cep/lookup?cep=${cleanCep}`, "GET");
      const data = res?.data || res;

      // Atualiza os campos de endereço
      const updatedDetails = {
        endereco: data.logradouro || "",
        bairro: data.bairro || "",
        cidade: data.city || data.localidade || "",
        uf: data.uf || "",
      };

      setCustomerDetails((prev) => ({ ...prev, ...updatedDetails }));
      debouncedUpdate(updatedDetails);
      setErrors((prevErrors) => ({ ...prevErrors, cep: "" })); // Remove erro de CEP, se existir
    } catch (error) {
      setErrors((prevErrors) => ({ ...prevErrors, cep: "Erro ao buscar o endereço" }));
    } finally {
      setIsFetchingAddress(false);
    }
  };

  const toggleAdditionalFields = () => {
    setShowAdditionalFields((prev) => !prev);
  };

  const externalRegistrationUrl = useExternalRegistrationUrl(selectedConversation?.id_interno);

  return (
    <Grid container spacing={2}>
      {/* Campos principais */}
      <Grid item xs={12}>
        <TextField
          label={isClientPJ ? "Nome fantasia" : "Nome"}
          fullWidth
          value={customerDetails.nome}
          onChange={(e) => handleFieldChange("nome", e.target.value)}
          error={!!errors.nome}
          helperText={errors.nome}
          InputProps={{
            startAdornment: (() => {
              const avatarSrc = getClientProfilePictureUrl(customerDetails.imagem);
              const initialsSource =
                getClientCommercialName({
                  ...customerDetails,
                  tipo: isClientPJ ? 1 : 0,
                }) || customerDetails.nome;
              return (
                <Avatar src={avatarSrc || undefined} sx={{ mr: 1 }}>
                  {!avatarSrc && getInitials(initialsSource)}
                </Avatar>
              );
            })(),
          }}
        />
      </Grid>
      {isClientPJ ? (
        <Grid item xs={12}>
          <TextField
            label="Razão social"
            fullWidth
            value={customerDetails.razaosocial}
            onChange={(e) => handleFieldChange("razaosocial", e.target.value)}
            error={!!errors.razaosocial}
            helperText={errors.razaosocial}
          />
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <TextField
          label="E-mail"
          fullWidth
          value={customerDetails.email}
          onChange={(e) => handleFieldChange("email", e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          InputProps={{
            startAdornment: <EmailIcon sx={{ mr: 1 }} />,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <PhoneField
          name="telefone"
          label="Telefone"
          value={customerDetails.telefone}
          onChange={(e) => handleFieldChange("telefone", e.target.value)}
          error={!!errors.telefone}
          helperText={errors.telefone}
          InputProps={{
            startAdornment: <PhoneIcon sx={{ mr: 1 }} />,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <PhoneField
          name="celular"
          label="Celular"
          value={customerDetails.celular}
          onChange={(e) => handleFieldChange("celular", e.target.value)}
          error={!!errors.celular}
          helperText={errors.celular}
          InputProps={{
            startAdornment: <PhoneIcon sx={{ mr: 1 }} />,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <PhoneField
          name="whatsapp"
          label="WhatsApp"
          value={customerDetails.whatsapp}
          onChange={(e) => handleFieldChange("whatsapp", e.target.value)}
          error={!!errors.whatsapp}
          helperText={errors.whatsapp}
          InputProps={{
            startAdornment: <PhoneIcon sx={{ mr: 1 }} />,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        {groups.length === 0 ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Nível de consciência
            </Typography>
            <Box
              onClick={(e) => setNivelConscienciaAnchor(e.currentTarget)}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                px: 1.5,
                py: 1.25,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                Nenhum nível cadastrado — clique para saber mais
              </Typography>
            </Box>
            <Popover
              open={Boolean(nivelConscienciaAnchor)}
              anchorEl={nivelConscienciaAnchor}
              onClose={() => setNivelConscienciaAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{ sx: { p: 2, maxWidth: 360 } }}
            >
              <Typography variant="subtitle2" gutterBottom>
                O que são níveis de consciência?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {NIVEL_CONSCIENCIA_EXPLICACAO}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SettingsIcon />}
                onClick={() => {
                  setNivelConscienciaAnchor(null);
                  navigate("/awareness-stages");
                }}
              >
                Cadastrar níveis de consciência
              </Button>
            </Popover>
          </>
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel id="nivel-consciencia-label">Nível de consciência</InputLabel>
            <Select
              labelId="nivel-consciencia-label"
              label="Nível de consciência"
              value={customerDetails.id_grupo ?? ""}
              onChange={(e) => handleFieldChange("id_grupo", e.target.value ? Number(e.target.value) : null)}
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.nome || `Grupo ${g.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Grid>

      {/* Tags do cliente */}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          {(customerDetails.tags || []).map((tag, index) => (
            <Chip
              key={`${tag}-${index}`}
              label={tag}
              onDelete={() => handleRemoveTag(index)}
              sx={{ mb: 0.5 }}
            />
          ))}
          <Autocomplete
            options={allowedTagNames}
            value={null}
            inputValue={tagInput}
            onInputChange={(_e, v) => {
              setTagInput(v);
              setTagSearch(v);
            }}
            onChange={(_e, value) => {
              if (!value) return;
              setTagInput("");
              applyTags([...(customerDetails.tags || []), value]);
            }}
            sx={{ minWidth: 220, flex: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Selecione uma tag"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      <IconButton
                        size="small"
                        onClick={handleAddTag}
                        aria-label="Adicionar tag"
                        sx={{ mr: 0.5 }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </Grid>

      {clientId && relationships.length > 0 ? (
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            Relacionamentos ({relationships.length})
          </Typography>
          <Stack spacing={1}>
            {relationships.map((rel) => {
              const peer = getClientRelationshipPeer(rel, clientId);
              if (!peer) return null;
              const direction = getClientRelationshipDirectionLabel(rel, clientId);
              return (
                <Paper
                  key={rel.id}
                  variant="outlined"
                  onClick={() => navigate(`/clients/${peer.clientId}`)}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "box-shadow 120ms ease, border-color 120ms ease",
                    "&:hover": { boxShadow: 1, borderColor: "primary.light" },
                  }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={getClientRelationshipTypeLabel(rel.relationship_type)} />
                    <Chip size="small" variant="outlined" label={direction} />
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 0 }}>
                      {peer.nome || `Cliente #${peer.clientId}`}
                    </Typography>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Grid>
      ) : null}

      <Snackbar
        open={tagSnack.open}
        autoHideDuration={4000}
        onClose={() => setTagSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setTagSnack((p) => ({ ...p, open: false }))}>
          {tagSnack.message}
        </Alert>
      </Snackbar>

      {!fieldDefsLoading &&
        getVisibleFieldDefinitions(fieldDefs?.client).map((def) => (
          <Grid item xs={12} key={def.key}>
            <CustomFieldRow
              definition={def}
              values={clientCustomValues}
              onCommit={handleClientCustomFieldCommit}
              disabled={updateMutation.isLoading}
            />
          </Grid>
        ))}

      {/* Botão para expandir/colapsar */}
      <Grid
        item
        xs={12}
        textAlign="center"
        sx={{
          margin: 0, // Remove margem no Grid
          padding: 0, // Remove padding no Grid
          paddingTop: 0,
        }}
      >
        <IconButton 
          onClick={toggleAdditionalFields} 
          size="small" // Reduz o tamanho do botão
          sx={{
            m: 0, // Remove margem do IconButton
            p: 0.5, // Reduz padding do IconButton
          }}
        >
          {showAdditionalFields ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Grid>


      {/* Campos adicionais */}
      {showAdditionalFields && <>
        <Grid item xs={12}>
          <InputMask
            mask="99999-999"
            value={customerDetails.cep}
            onChange={(e) => handleFieldChange("cep", e.target.value)}
          >
            {(inputProps) => (
              <TextField
                {...inputProps}
                label="CEP"
                fullWidth
                error={!!errors.cep}
                helperText={errors.cep || (isFetchingAddress ? "Carregando endereço..." : "")}
              />
            )}
          </InputMask>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Endereço"
            fullWidth
            value={customerDetails.endereco}
            onChange={(e) => handleFieldChange("endereco", e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Número"
            fullWidth
            value={customerDetails.numero}
            onChange={(e) => handleFieldChange("numero", e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Bairro"
            fullWidth
            value={customerDetails.bairro}
            onChange={(e) => handleFieldChange("bairro", e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Cidade"
            fullWidth
            value={customerDetails.cidade}
            onChange={(e) => handleFieldChange("cidade", e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="UF"
            fullWidth
            value={customerDetails.uf}
            onChange={(e) => handleFieldChange("uf", e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="CPF"
            fullWidth
            value={customerDetails.cpf}
            onChange={(e) => handleFieldChange("cpf", e.target.value)}
          />
        </Grid>
        {isClientPJ ? (
          <>
            <Grid item xs={6}>
              <TextField
                label="CNPJ"
                fullWidth
                value={customerDetails.cnpj}
                onChange={(e) => handleFieldChange("cnpj", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Data de abertura"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={customerDetails.data_abertura}
                onChange={(e) => handleFieldChange("data_abertura", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Inscrição estadual"
                fullWidth
                disabled={!!Number(customerDetails.inscricao_estadual_isento)}
                value={
                  Number(customerDetails.inscricao_estadual_isento)
                    ? ""
                    : customerDetails.inscricao_estadual
                }
                placeholder={
                  Number(customerDetails.inscricao_estadual_isento) ? "Isento" : undefined
                }
                onChange={(e) => handleFieldChange("inscricao_estadual", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="cliente-ie-isento-label">IE isento</InputLabel>
                <Select
                  labelId="cliente-ie-isento-label"
                  label="IE isento"
                  value={Number(customerDetails.inscricao_estadual_isento) ? "1" : "0"}
                  onChange={(e) =>
                    handleFieldChange("inscricao_estadual_isento", Number(e.target.value))
                  }
                >
                  <MenuItem value="0">Não</MenuItem>
                  <MenuItem value="1">Sim</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Responsável"
                fullWidth
                value={customerDetails.nome_responsavel}
                onChange={(e) => handleFieldChange("nome_responsavel", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Cargo do responsável"
                fullWidth
                value={customerDetails.cargo_responsavel}
                onChange={(e) => handleFieldChange("cargo_responsavel", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tamanho da frota"
                fullWidth
                value={customerDetails.tamanho_frota}
                onChange={(e) => handleFieldChange("tamanho_frota", e.target.value)}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={6}>
              <TextField
                label="Identidade"
                fullWidth
                value={customerDetails.identidade}
                onChange={(e) => handleFieldChange("identidade", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Data de nascimento"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={customerDetails.data_nascimento}
                onChange={(e) => handleFieldChange("data_nascimento", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="cliente-sexo-label">Sexo</InputLabel>
                <Select
                  labelId="cliente-sexo-label"
                  label="Sexo"
                  value={customerDetails.sexo || ""}
                  onChange={(e) => handleFieldChange("sexo", e.target.value)}
                >
                  <MenuItem value="">
                    <em>Não informado</em>
                  </MenuItem>
                  <MenuItem value="Masculino">Masculino</MenuItem>
                  <MenuItem value="Feminino">Feminino</MenuItem>
                  <MenuItem value="Outro">Outro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Redes sociais
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Facebook"
            fullWidth
            value={customerDetails.id_facebook}
            onChange={(e) => handleFieldChange("id_facebook", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Instagram"
            fullWidth
            value={customerDetails.id_instagram}
            onChange={(e) => handleFieldChange("id_instagram", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="LinkedIn"
            fullWidth
            value={customerDetails.id_linkedin}
            onChange={(e) => handleFieldChange("id_linkedin", e.target.value)}
          />
        </Grid>
      </>}

      {/* Botão para cadastro completo */}
      {externalRegistrationUrl ? (
        <Grid item xs={12}>
          <ExternalRegistrationButton
            idInterno={selectedConversation?.id_interno}
            fullWidth
          />
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          href={`/clients/${selectedConversation?.clientId}`}
        >
          Ver Cadastro Completo
        </Button>
      </Grid>

      <DuplicateClientDialog
        open={dupDialog.open}
        duplicates={dupDialog.duplicates}
        attemptedPayload={dupDialog.attemptedPayload}
        secondaryClientId={dupDialog.secondaryClientId}
        secondaryLabel="cliente desta conversa"
        onClose={() => setDupDialog((s) => ({ ...(s || {}), open: false }))}
        onChangePhone={() => setDupDialog((s) => ({ ...(s || {}), open: false }))}
        onMerged={() => setDupDialog((s) => ({ ...(s || {}), open: false }))}
      />
    </Grid>
  );
};

export default CustomerDetails;
